import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useViewer } from '@/features/session';
import type { OAuthProvider } from '@/shared/config/routes';
import { track } from '@/shared/lib/analytics';
import { initialSignInView } from '../model/oauth-message';
import type { SignInLinkData, SignInView } from '../types';
import { useOAuthPopup } from './use-oauth-popup';

type UseSignInFlowResult = {
  view: SignInView;
  viewKey: string;
  showForm: () => void;
  showSent: (link: SignInLinkData, isResent: boolean) => void;
  startOAuth: (provider: OAuthProvider) => void;
  cancelOAuth: () => void;
};

/** The sign-in pane's views. Once a session exists the GuestGuard moves the page on by itself. */
export function useSignInFlow(): UseSignInFlowResult {
  const [searchParams] = useSearchParams();
  const [view, setView] = useState<SignInView>(() => initialSignInView(searchParams));
  const { refetch, refreshViewer } = useViewer();

  const checkSignedIn = useCallback(async () => (await refreshViewer()) !== null, [refreshViewer]);

  const { startOAuth: openPopup, cancelOAuth: closePopup } = useOAuthPopup({
    onSignedIn: (provider) => {
      track('login', { method: provider });
      void refetch();
    },
    onFailed: (provider) => setView({ name: 'oauth-error', provider }),
    checkSignedIn,
  });

  const startOAuth = useCallback(
    (provider: OAuthProvider) => {
      if (openPopup(provider)) setView({ name: 'oauth', provider });
    },
    [openPopup],
  );

  const cancelOAuth = useCallback(() => {
    closePopup();
    setView({ name: 'form' });
  }, [closePopup]);

  const showForm = useCallback(() => setView({ name: 'form' }), []);
  const showSent = useCallback(
    (link: SignInLinkData, isResent: boolean) => setView({ name: 'sent', link, isResent }),
    [],
  );

  const viewKey = view.name === 'sent' ? `sent-${view.link.id}` : view.name;
  return { view, viewKey, showForm, showSent, startOAuth, cancelOAuth };
}
